<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { routerTabMessages } from '../../messages';
import { useRouterTab, type TabItem } from './useRouterTab';

const props = withDefaults(
  defineProps<{
    homeRoute?: string;
    homeTitle?: string;
    homeIcon?: string;
  }>(),
  {
    homeRoute: '/',
    homeTitle: 'Dashboard',
  },
);

const route = useRoute();
const router = useRouter();

const {
  tabs,
  addTab,
  closeTab,
  closeOtherTabs,
  closeAllTabs,
  closeLeftTabs,
  closeRightTabs,
  hasClosableLeftOf,
  hasClosableRightOf,
  refreshTab,
  refreshAllTabs,
  getCacheKey,
  getTabView,
  reorderTabs,
  updateTabTitle,
  updateTabIcon,
  cachedViews,
} = useRouterTab({
  homeRoute: props.homeRoute,
  homeTitle: props.homeTitle,
  homeIcon: props.homeIcon,
});

const contextMenuEl = ref<HTMLElement | null>(null);

const draggedTab = ref<TabItem | null>(null);
const dragOverIndex = ref<number | null>(null);

const contextMenu = ref<{
  show: boolean;
  x: number;
  y: number;
  tab: TabItem | null;
}>({
  show: false,
  x: 0,
  y: 0,
  tab: null,
});

const canCloseLeft = computed(() => {
  const tab = contextMenu.value.tab;
  return !!tab && hasClosableLeftOf(tab.fullPath);
});

const canCloseRight = computed(() => {
  const tab = contextMenu.value.tab;
  return !!tab && hasClosableRightOf(tab.fullPath);
});

watch(
  () => route.fullPath,
  () => {
    addTab();
  },
  { immediate: true },
);

const onRouteTabUpdate = (event: Event) => {
  const detail = (event as CustomEvent).detail;
  if (!detail?.path) {
    return;
  }

  if (detail.title) {
    updateTabTitle(detail.path, detail.title);
  }
  if (detail.icon) {
    updateTabIcon(detail.path, detail.icon);
  }
};

const cacheKey = computed(() => getCacheKey(route));

const isActive = (fullPath: string) => route.fullPath === fullPath;

const navigateTo = (fullPath: string) => {
  router.push(fullPath);
};

const onWheel = (event: WheelEvent) => {
  if (event.deltaY !== 0 && event.deltaX === 0) {
    event.preventDefault();
    const container = event.currentTarget as HTMLElement;
    container.scrollLeft += event.deltaY;
  }
};

const onDragStart = (event: DragEvent, tab: TabItem, index: number) => {
  if (!tab.closable) {
    event.preventDefault();
    return;
  }
  draggedTab.value = tab;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', tab.fullPath);
  }
};

const onDragOver = (event: DragEvent, index: number) => {
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'move';
  }
  dragOverIndex.value = index;
};

const onDragLeave = () => {
  dragOverIndex.value = null;
};

const onDrop = (event: DragEvent, targetIndex: number) => {
  event.preventDefault();
  if (!draggedTab.value) {
    return;
  }

  const fromIndex = tabs.value.findIndex(
    (t) => t.fullPath === draggedTab.value?.fullPath,
  );
  if (fromIndex !== -1 && fromIndex !== targetIndex) {
    reorderTabs(fromIndex, targetIndex);
  }

  draggedTab.value = null;
  dragOverIndex.value = null;
};

const onDragEnd = () => {
  draggedTab.value = null;
  dragOverIndex.value = null;
};

const onContextMenu = async (event: MouseEvent, tab: TabItem) => {
  event.preventDefault();

  contextMenu.value = {
    show: true,
    x: event.clientX,
    y: event.clientY,
    tab,
  };

  await nextTick();

  if (contextMenuEl.value) {
    const menuRect = contextMenuEl.value.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (contextMenu.value.x + menuRect.width > viewportWidth) {
      contextMenu.value.x = viewportWidth - menuRect.width - 8;
    }

    if (contextMenu.value.y + menuRect.height > viewportHeight) {
      contextMenu.value.y = viewportHeight - menuRect.height - 8;
    }
  }
};

const closeContextMenu = () => {
  contextMenu.value.show = false;
  contextMenu.value.tab = null;
};

const handleContextAction = async (action: string) => {
  const tab = contextMenu.value.tab;
  if (!tab) {
    return;
  }

  switch (action) {
    case 'close':
      closeTab(tab.fullPath);
      break;
    case 'closeOthers':
      closeOtherTabs(tab.fullPath);
      break;
    case 'closeAll':
      closeAllTabs();
      break;
    case 'closeLeft':
      closeLeftTabs(tab.fullPath);
      break;
    case 'closeRight':
      closeRightTabs(tab.fullPath);
      break;
    case 'refresh':
      await refreshTab(tab.fullPath);
      break;
    case 'refreshAll':
      await refreshAllTabs();
      break;
  }
  closeContextMenu();
};

onMounted(() => {
  document.addEventListener('click', closeContextMenu);
  window.addEventListener('itp:routeTab', onRouteTabUpdate);
});

onUnmounted(() => {
  document.removeEventListener('click', closeContextMenu);
  window.removeEventListener('itp:routeTab', onRouteTabUpdate);
});
</script>

<template>
  <div class="router-tab d-flex flex-column h-100">
    <div class="router-tab__header">
      <div class="router-tab__scroll" @wheel="onWheel">
        <div class="router-tab__scroll-container d-flex">
          <div
            v-for="(tab, index) in tabs"
            :key="tab.id"
            class="router-tab__item"
            :class="{
              'is-active': isActive(tab.fullPath),
              'is-closable': tab.closable,
              'is-drag-over': dragOverIndex === index,
            }"
            :draggable="tab.closable"
            @click="navigateTo(tab.fullPath)"
            @contextmenu="onContextMenu($event, tab)"
            @dragstart="onDragStart($event, tab, index)"
            @dragover="onDragOver($event, index)"
            @dragleave="onDragLeave"
            @drop="onDrop($event, index)"
            @dragend="onDragEnd"
          >
            <ItpIcon
              v-if="tab.icon"
              :name="tab.icon"
              class="router-tab__item-icon"
            />
            <span class="router-tab__item-title" :title="tab.title">
              {{ tab.title }}
            </span>
            <i
              v-if="tab.closable"
              class="router-tab__item-close"
              @click.prevent.stop="closeTab(tab.fullPath)"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="router-tab__container flex-grow-1 overflow-auto position-relative"
    >
      <router-view v-slot="{ Component }">
        <keep-alive :include="cachedViews">
          <component :is="getTabView(cacheKey)" :key="cacheKey">
            <component :is="Component" />
          </component>
        </keep-alive>
      </router-view>
      <ItpIframeKeepAliveHost />
    </div>

    <Teleport to="body">
      <div
        v-if="contextMenu.show"
        ref="contextMenuEl"
        class="router-tab-context-menu"
        :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      >
        <div
          v-if="contextMenu.tab?.closable"
          class="context-menu-item"
          @click="handleContextAction('close')"
        >
          {{ routerTabMessages.close }}
        </div>
        <div
          v-if="canCloseLeft"
          class="context-menu-item"
          @click="handleContextAction('closeLeft')"
        >
          {{ routerTabMessages.closeToTheLeft }}
        </div>
        <div
          v-if="canCloseRight"
          class="context-menu-item"
          @click="handleContextAction('closeRight')"
        >
          {{ routerTabMessages.closeToTheRight }}
        </div>
        <div
          class="context-menu-item"
          @click="handleContextAction('closeOthers')"
        >
          {{ routerTabMessages.closeOthers }}
        </div>
        <div class="context-menu-item" @click="handleContextAction('closeAll')">
          {{ routerTabMessages.closeAll }}
        </div>
        <div class="context-menu-divider"></div>
        <div class="context-menu-item" @click="handleContextAction('refresh')">
          {{ routerTabMessages.refresh }}
        </div>
        <div
          class="context-menu-item"
          @click="handleContextAction('refreshAll')"
        >
          {{ routerTabMessages.refreshAll }}
        </div>
      </div>
    </Teleport>
  </div>
</template>
